/* ---------------------------------------------------------------------------
 * Showcase — shared auth design primitives
 * ---------------------------------------------------------------------------
 * The "Showcase" direction (prostay-website "Linear-light" language): a warm
 * mesh hero panel whose product shot auto-rotates through Prostay's modules,
 * beside a clean white form panel.
 *
 * This module is the single source of truth used by BOTH the throwaway preview
 * gallery (AuthRedesign.js) and the real auth pages (LoginPage, ForgotPassword,
 * FAAuthPage). Styling lives in authRedesign.css.
 * ------------------------------------------------------------------------- */
import React, { useEffect, useRef, useState } from 'react';
import { Input } from '../../ui/components';
import { Icon } from '../../ui/icons';
import './authRedesign.css';

/* The real Prostay wordmark (vector identical to the website's V2Logo). */
export function Wordmark({ className = '', width = 104, height = 26, color = 'currentColor' }) {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 102 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Prostay"
    >
      <title>Prostay</title>
      <path d="M0.438361 25.5C0.156558 25.5 0 25.3148 0 25.037V4.97531C0 4.69753 0.156558 4.51235 0.438361 4.51235H2.7241C3.00591 4.51235 3.16246 4.69753 3.16246 4.97531L3.22509 7.01235C4.28968 5.25309 6.10575 4.14197 8.64198 4.14197C13.0256 4.14197 16.0315 7.59876 16.0315 12.0741C16.0315 16.5802 13.0256 20.0062 8.64198 20.0062C6.2623 20.0062 4.47755 19.0185 3.38164 17.3827V25.037C3.38164 25.3148 3.22509 25.5 2.94328 25.5H0.438361ZM3.35033 12.1049C3.35033 15.0062 5.26034 16.9506 7.98444 16.9506C10.7399 16.9506 12.7438 15.0062 12.7438 12.0741C12.7438 9.17284 10.7399 7.19753 7.98444 7.19753C5.26034 7.19753 3.35033 9.17284 3.35033 12.1049Z" fill={color} />
      <path d="M18.3202 19.6358C18.0384 19.6358 17.8818 19.4506 17.8818 19.1728V4.97531C17.8818 4.69753 18.0384 4.51235 18.3202 4.51235H20.6999C20.9817 4.51235 21.1382 4.6358 21.1382 5.00617L21.2321 6.82716C21.7958 5.31481 22.7977 4.14197 24.6451 4.14197C25.334 4.14197 25.8036 4.2963 26.0541 4.48148C26.2733 4.6358 26.3359 4.79012 26.3359 5.0679V7.04321C26.3359 7.38272 26.1167 7.47531 25.741 7.38272C25.3653 7.25926 25.0208 7.22839 24.6138 7.22839C22.2028 7.22839 21.2635 9.08025 21.2635 11.0247V19.1728C21.2635 19.4506 21.1069 19.6358 20.8251 19.6358H18.3202Z" fill={color} />
      <path d="M34.5251 20.0062C29.9536 20.0062 26.572 16.5494 26.572 12.0741C26.572 7.59876 29.9536 4.14197 34.5251 4.14197C39.0653 4.14197 42.4469 7.59876 42.4469 12.0741C42.4469 16.5494 39.0653 20.0062 34.5251 20.0062ZM29.8284 12.0741C29.8284 14.9753 31.8636 16.9198 34.5251 16.9198C37.1553 16.9198 39.1905 14.9753 39.1905 12.0741C39.1905 9.17284 37.1553 7.22839 34.5251 7.22839C31.8636 7.22839 29.8284 9.17284 29.8284 12.0741Z" fill={color} />
      <path d="M48.8675 20.0062C46.3626 20.0062 44.5465 18.8642 43.4819 17.5988C43.294 17.3827 43.294 17.0741 43.5132 16.7654L44.3273 15.6543C44.5465 15.3457 44.8283 15.3765 45.0788 15.5926C46.206 16.5494 47.3019 17.1667 48.7109 17.1667C50.0886 17.1667 51.0593 16.6111 51.0593 15.4074C51.0593 14.358 50.3391 13.8642 49.0867 13.3395L47.208 12.5988C45.2667 11.8272 43.795 10.5926 43.795 8.46296C43.795 5.74691 46.1434 4.14197 48.8675 4.14197C50.7462 4.14197 52.4996 4.91358 53.6895 6.3642C53.8773 6.58025 53.846 6.82716 53.6582 7.01235L52.6875 8.15432C52.437 8.4321 52.2178 8.40123 51.936 8.15432C50.9654 7.25926 50.026 6.82716 49.0241 6.82716C47.8342 6.82716 47.0201 7.41358 47.0201 8.4321C47.0201 9.48148 47.8342 9.94444 49.0241 10.4074L50.8714 11.1481C52.8441 11.9198 54.4096 13.1852 54.4096 15.5C54.3783 18.463 51.8108 20.0062 48.8675 20.0062Z" fill={color} />
      <path d="M61.2593 19.821C58.9735 19.821 57.2514 18.7407 57.2514 15.9321V7.16667H55.2788C54.997 7.16667 54.8404 6.98148 54.8404 6.7037V4.97531C54.8404 4.69753 54.997 4.51235 55.2788 4.51235H57.2514V0.962963C57.2514 0.685185 57.408 0.5 57.6898 0.5H60.226C60.5078 0.5 60.6644 0.685185 60.6644 0.962963V4.51235H63.138C63.4198 4.51235 63.6076 4.69753 63.6076 4.97531V6.7037C63.6076 6.98148 63.4198 7.16667 63.138 7.16667H60.6644V15.5309C60.6644 16.5494 61.1653 16.9815 62.0421 16.9815C62.2926 16.9815 62.6683 16.9815 63.0127 16.9506C63.3885 16.8889 63.5763 17.0432 63.5763 17.3519V19.0185C63.5763 19.6358 62.1986 19.821 61.2593 19.821Z" fill={color} />
      <path d="M71.7371 20.0062C67.3535 20.0062 64.3476 16.5802 64.3476 12.0741C64.3476 7.59876 67.3535 4.14197 71.7371 4.14197C74.2734 4.14197 76.0894 5.25309 77.154 7.01235L77.2167 4.97531C77.2167 4.69753 77.3732 4.51235 77.655 4.51235H79.9408C80.2226 4.51235 80.3791 4.69753 80.3791 4.97531V19.1728C80.3791 19.4506 80.2226 19.6358 79.9408 19.6358H77.655C77.3732 19.6358 77.2167 19.4506 77.2167 19.1728L77.154 17.1667C76.0581 18.9259 74.2421 20.0062 71.7371 20.0062ZM67.6353 12.0741C67.6353 15.0062 69.6393 16.9506 72.3947 16.9506C75.1188 16.9506 77.0288 15.0062 77.0288 12.1049C77.0288 9.17284 75.1188 7.19753 72.3947 7.19753C69.6393 7.19753 67.6353 9.17284 67.6353 12.0741Z" fill={color} />
      <path d="M86.1747 25.5C85.8303 25.5 85.6737 25.3148 85.8302 25.0062L88.0221 19.6667L82.1042 5.03704C81.9789 4.69753 82.1355 4.51235 82.4799 4.51235H85.0162C85.2666 4.51235 85.4545 4.6358 85.5484 4.88272L89.6816 15.8086L93.846 4.88272C93.9399 4.6358 94.1278 4.51235 94.3783 4.51235H96.9145C97.259 4.51235 97.3842 4.69753 97.259 5.03704L89.1806 25.1296C89.0867 25.3765 88.8675 25.5 88.617 25.5H86.1747Z" fill={color} />
      <path d="M98.7022 18.8184C100.518 18.8184 102 20.2682 102 22.0577C102 23.8473 100.518 25.2971 98.7022 25.2971C96.8867 25.2971 95.4045 23.8473 95.4045 22.0577C95.4045 20.2682 96.8867 18.8184 98.7022 18.8184ZM98.7022 24.5156C100.081 24.5156 101.161 23.4169 101.161 22.0577C101.161 20.6873 100.081 19.5886 98.7022 19.5886C97.3234 19.5886 96.2318 20.6873 96.2318 22.0577C96.2318 23.4169 97.3234 24.5156 98.7022 24.5156ZM100.288 21.5481C100.288 22.1144 99.9317 22.4542 99.3572 22.5561L100.253 23.4962C100.299 23.5528 100.276 23.6208 100.196 23.6208H99.5985C99.541 23.6208 99.5066 23.5981 99.4721 23.5641L98.5758 22.5901H98.0013V23.5188C98.0013 23.5755 97.9668 23.6208 97.9094 23.6208H97.4038C97.3464 23.6208 97.3119 23.5755 97.3119 23.5188V20.5853C97.3119 20.5287 97.3464 20.4834 97.4038 20.4834H98.9665C99.7594 20.4834 100.288 20.8345 100.288 21.5481ZM98.978 22.0011C99.3802 22.0011 99.5755 21.8086 99.5755 21.5481C99.5755 21.2649 99.3802 21.0837 98.978 21.0837H98.0013V22.0011H98.978Z" fill={color} />
    </svg>
  );
}

export function Brandmark({ light = false }) {
  // TableView wordmark. The auth showcase was ported from the Prostay website, so the
  // vector Wordmark above is Prostay's — the POS must draw its OWN name here, or the
  // sign-in screen carries the wrong brand. Rendered as text (inherits panel colour).
  return (
    <span className={`authx-brand${light ? ' authx-brand--light' : ''}`}>
      <svg width="152" height="26" viewBox="0 0 152 26" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="TableView">
        <title>TableView</title>
        <text
          x="0"
          y="19"
          fontFamily="-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif"
          fontSize="22"
          fontWeight="700"
          letterSpacing="-0.6"
          fill="currentColor"
        >
          TableView
        </text>
      </svg>
    </span>
  );
}

export function Eyebrow({ children }) {
  return <span className="authx-eyebrow">{children}</span>;
}

/* A mock "window into the product" frame; each module supplies a scene. */
export function Shell({ tab, children }) {
  return (
    <div className="authx-shell" role="img" aria-label="Prostay product preview">
      <div className="authx-shell__bar">
        <span className="authx-shell__dot" />
        <span className="authx-shell__dot" />
        <span className="authx-shell__dot" />
        <span className="authx-shell__tab">{tab}</span>
      </div>
      <div className="authx-shell__body">{children}</div>
    </div>
  );
}

export function ReservationsScene() {
  return (
    <>
      <div className="authx-stats">
        <div className="authx-stat">
          <span className="authx-stat__label">Occupancy</span>
          <span className="authx-stat__value">82<small>%</small></span>
          <span className="authx-stat__trend authx-stat__trend--up">+4.2%</span>
        </div>
        <div className="authx-stat">
          <span className="authx-stat__label">ADR</span>
          <span className="authx-stat__value">€186</span>
          <span className="authx-stat__trend authx-stat__trend--up">+1.8%</span>
        </div>
        <div className="authx-stat">
          <span className="authx-stat__label">Arrivals</span>
          <span className="authx-stat__value">37</span>
          <span className="authx-stat__trend">12 in-house</span>
        </div>
      </div>

      <div className="authx-grid">
        <div className="authx-grid__head">
          <span>Room</span>
          <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span>
        </div>
        {[
          { r: '101 · Deluxe', cells: [2, 2, 2, 0, 0] },
          { r: '102 · Deluxe', cells: [0, 1, 1, 1, 0] },
          { r: '201 · Suite', cells: [3, 3, 0, 0, 2] },
          { r: '202 · Suite', cells: [0, 0, 1, 1, 1] },
        ].map((row) => (
          <div key={row.r} className="authx-grid__row">
            <span className="authx-grid__room">{row.r}</span>
            {row.cells.map((c, i) => (
              <span key={i} className={`authx-cell authx-cell--${c}`} />
            ))}
          </div>
        ))}
      </div>

      <div className="authx-resv">
        <span className="authx-resv__avatar">MR</span>
        <div className="authx-resv__meta">
          <span className="authx-resv__name">Maya Robinson</span>
          <span className="authx-resv__sub">2 nights · Suite 201 · checked in</span>
        </div>
        <span className="authx-resv__badge">Paid</span>
      </div>
    </>
  );
}

export function HousekeepingScene() {
  const rows = [
    { room: '101 · Deluxe', who: 'Ana M.', status: 'clean', label: 'Clean' },
    { room: '102 · Deluxe', who: 'Ravi K.', status: 'progress', label: 'In progress' },
    { room: '201 · Suite', who: 'Mia L.', status: 'dirty', label: 'Dirty' },
    { room: '202 · Suite', who: 'Ravi K.', status: 'inspected', label: 'Inspected' },
    { room: '305 · Twin', who: 'Ana M.', status: 'clean', label: 'Clean' },
  ];
  return (
    <>
      <div className="authx-hk__summary">
        <span className="authx-chip authx-chip--ok">18 Clean</span>
        <span className="authx-chip authx-chip--warn">6 In progress</span>
        <span className="authx-chip authx-chip--bad">4 Dirty</span>
      </div>
      <div className="authx-hk">
        {rows.map((r) => (
          <div className="authx-hk__row" key={r.room}>
            <span className="authx-hk__room">{r.room}</span>
            <span className="authx-hk__who">{r.who}</span>
            <span className={`authx-status authx-status--${r.status}`}>{r.label}</span>
          </div>
        ))}
      </div>
    </>
  );
}

export function PayScene() {
  return (
    <>
      <div className="authx-pay">
        <div className="authx-pay__head">
          <span className="authx-pay__label">Folio #1042 · Maya Robinson</span>
          <span className="authx-resv__badge">Paid</span>
        </div>
        <div className="authx-pay__amount">€1,240<small>.00</small></div>
        <div className="authx-pay__method">
          <span className="authx-pay__card">VISA</span>
          <span>•••• 4242</span>
          <span className="authx-pay__sep">·</span>
          <span>Charged today</span>
        </div>
      </div>
      <div className="authx-paylist">
        {[
          { ref: 'Payment link', who: 'Liam Carter', amt: '€480.00', state: 'ok', label: 'Paid' },
          { ref: 'Card terminal', who: 'Sofia Diaz', amt: '€96.50', state: 'ok', label: 'Paid' },
          { ref: 'Payment link', who: 'N. Okafor', amt: '€312.00', state: 'pending', label: 'Pending' },
        ].map((p) => (
          <div className="authx-payrow" key={p.who}>
            <div className="authx-payrow__meta">
              <span className="authx-payrow__who">{p.who}</span>
              <span className="authx-payrow__ref">{p.ref}</span>
            </div>
            <span className="authx-payrow__amt">{p.amt}</span>
            <span className={`authx-status authx-status--${p.state === 'ok' ? 'clean' : 'progress'}`}>{p.label}</span>
          </div>
        ))}
      </div>
    </>
  );
}

export function AiScene() {
  return (
    <div className="authx-chat">
      <div className="authx-bubble authx-bubble--guest">Hi! Could we get a late check-out tomorrow?</div>
      <div className="authx-bubble authx-bubble--ai">
        Of course — I&apos;ve set check-out for 2pm on your Suite 201. Would you like a spa slot added?
      </div>
      <div className="authx-bubble authx-bubble--guest">Yes please, around noon.</div>
      <div className="authx-bubble authx-bubble--ai authx-bubble--typing" aria-label="Prostay AI is typing">
        <span className="authx-typing"><span /><span /><span /></span>
      </div>
    </div>
  );
}

export function ChannelsScene() {
  const ch = [
    { name: 'Booking.com', rate: '€186', avail: 12, sync: 'ok', label: 'Synced' },
    { name: 'Airbnb', rate: '€192', avail: 8, sync: 'ok', label: 'Synced' },
    { name: 'Expedia', rate: '€189', avail: 5, sync: 'progress', label: 'Updating' },
    { name: 'Agoda', rate: '€184', avail: 9, sync: 'ok', label: 'Synced' },
  ];
  return (
    <>
      <div className="authx-stats authx-stats--2">
        <div className="authx-stat">
          <span className="authx-stat__label">Channels live</span>
          <span className="authx-stat__value">7</span>
          <span className="authx-stat__trend authx-stat__trend--up">All in sync</span>
        </div>
        <div className="authx-stat">
          <span className="authx-stat__label">Last push</span>
          <span className="authx-stat__value">2<small>m ago</small></span>
          <span className="authx-stat__trend">No conflicts</span>
        </div>
      </div>
      <div className="authx-ch">
        <div className="authx-ch__head">
          <span>Channel</span><span>Rate</span><span>Avail</span><span>Status</span>
        </div>
        {ch.map((c) => (
          <div className="authx-ch__row" key={c.name}>
            <span className="authx-ch__name">{c.name}</span>
            <span className="authx-ch__rate">{c.rate}</span>
            <span className="authx-ch__avail">{c.avail}</span>
            <span className={`authx-status authx-status--${c.sync === 'ok' ? 'clean' : 'progress'}`}>{c.label}</span>
          </div>
        ))}
      </div>
    </>
  );
}

/* The modules the Showcase preview rotates through, with copy synced to each. */
export const MODULES = [
  {
    id: 'reservations',
    tab: 'Front desk · Today',
    eyebrow: 'Reservations',
    title: 'See your whole house at a glance.',
    lead: 'Live availability, drag-and-drop bookings and rates — your front desk, always in sync.',
    Scene: ReservationsScene,
  },
  {
    id: 'housekeeping',
    tab: 'Housekeeping · Today',
    eyebrow: 'Housekeeping',
    title: 'Keep every room turn on track.',
    lead: 'Real-time room status, attendant assignments and inspections across the whole property.',
    Scene: HousekeepingScene,
  },
  {
    id: 'pay',
    tab: 'Prostay Pay · Folios',
    eyebrow: 'Prostay Pay',
    title: 'Get paid faster, right from the folio.',
    lead: 'Take cards, send secure payment links and reconcile folios without leaving Prostay.',
    Scene: PayScene,
  },
  {
    id: 'ai',
    tab: 'Prostay AI · Inbox',
    eyebrow: 'Prostay AI',
    title: 'Answer guests in seconds, not hours.',
    lead: 'AI drafts replies, handles requests and books upsells across every channel.',
    Scene: AiScene,
  },
  {
    id: 'channels',
    tab: 'Channel Manager · Live',
    eyebrow: 'Channel Manager',
    title: 'One inventory, every channel.',
    lead: 'Push rates and availability to every OTA and prevent overbookings automatically.',
    Scene: ChannelsScene,
  },
];

/* The left hero stage: auto-rotating product carousel with synced copy. */
export function ShowcaseStage() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const reduce = typeof window !== 'undefined'
      && window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const id = setInterval(() => {
      setActive((a) => (a + 1) % MODULES.length);
    }, 4600);
    return () => clearInterval(id);
  }, [paused]);

  const m = MODULES[active];
  const Scene = m.Scene;

  return (
    <aside
      className="authx-showcase__stage"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="authx-showcase__top">
        <Brandmark />
      </div>

      <div className="authx-showcase__copy" key={m.id}>
        <span className="authx-eyebrow authx-anim">{m.eyebrow}</span>
        <h2 className="authx-display authx-anim authx-anim--1">{m.title}</h2>
        <p className="authx-lead authx-anim authx-anim--2">{m.lead}</p>
      </div>

      <div className="authx-showcase__preview">
        <div className="authx-glow" aria-hidden="true" />
        <div className="authx-scene" key={m.id}>
          <Shell tab={m.tab}><Scene /></Shell>
        </div>
      </div>

      <div className="authx-dots" role="tablist" aria-label="Prostay modules">
        {MODULES.map((mod, i) => (
          <button
            key={mod.id}
            type="button"
            role="tab"
            aria-selected={i === active}
            aria-label={mod.eyebrow}
            title={mod.eyebrow}
            className={`authx-dot-btn${i === active ? ' is-active' : ''}`}
            onClick={() => setActive(i)}
          />
        ))}
      </div>
    </aside>
  );
}

/**
 * AuthShowcase — full-height standalone auth page used by the real pages.
 * Renders the rotating hero stage on the left and `children` (the form) on the
 * right.
 */
export function AuthShowcase({ children }) {
  return (
    <div className="authx-standalone">
      <div className="authx-showcase">
        <ShowcaseStage />
        <main className="authx-showcase__main">
          <div className="authx-panel">{children}</div>
        </main>
      </div>
    </div>
  );
}

/* Segmented 6-box one-time-code input. `value`/`onChange(code)` are optional;
 * when omitted it manages its own state (handy for previews). */
export function OtpInput({ length = 6, value, onChange }) {
  const [internal, setInternal] = useState(Array(length).fill(''));
  const controlled = Array.isArray(value);
  const vals = controlled
    ? Array.from({ length }, (_, i) => value[i] || '')
    : internal;
  const refs = useRef([]);

  const emit = (next) => {
    if (!controlled) setInternal(next);
    onChange?.(next.join(''));
  };

  const handleChange = (i, e) => {
    const v = e.target.value.replace(/\D/g, '').slice(-1);
    const next = [...vals];
    next[i] = v;
    emit(next);
    if (v && i < length - 1) refs.current[i + 1]?.focus();
  };
  const handleKey = (i, e) => {
    if (e.key === 'Backspace' && !vals[i] && i > 0) refs.current[i - 1]?.focus();
  };
  const handlePaste = (e) => {
    const text = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (!text) return;
    e.preventDefault();
    const next = Array(length).fill('');
    text.split('').forEach((c, idx) => { next[idx] = c; });
    emit(next);
    refs.current[Math.min(text.length, length - 1)]?.focus();
  };

  return (
    <div className="authx-otp" onPaste={handlePaste}>
      {vals.map((v, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          className="authx-otp__box"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={v}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKey(i, e)}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}

export function PasswordField({
  label = 'Password',
  placeholder = 'Enter your password',
  value,
  onChange,
  autoFocus,
  error,
}) {
  const [show, setShow] = useState(false);
  return (
    <Input
      label={label}
      type={show ? 'text' : 'password'}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      autoFocus={autoFocus}
      error={error}
      leadingIcon={<Icon.Lock size={16} />}
      trailingIcon={(
        <button
          type="button"
          className="authx-eye"
          aria-label={show ? 'Hide password' : 'Show password'}
          onClick={() => setShow((s) => !s)}
        >
          <Icon.Eye size={16} />
        </button>
      )}
    />
  );
}
